/**
 * The terminal is a small shell over the site, not a table of magic strings.
 *
 * One tree is both the site's destinations and the shell's filesystem: every
 * destination is a directory that always carries a `README.md`, and `about` also
 * carries the work history. `cd`, `ls`, `cat`, `open`, `pwd` and Tab completion
 * all read that tree through one path resolver, so the copy under a button
 * cannot promise something the shell cannot run: the hint is the command.
 *
 * The working directory is not stored. It is the current route's node, so a
 * link, a button, a `cd` and the browser's back button all leave the prompt in
 * the same place.
 *
 * A `cat` does not navigate and a `cd` prints nothing, as in a shell. Landing on
 * a route runs `cat README.md` there, which is what the rail has always
 * narrated.
 */

import { CATALOG, GITHUB_HANDLE, GITHUB_URL, PACKAGES, STUDIO, WORK } from './content'

export type To = '/' | '/about' | '/products' | '/projects' | '/tips' | '/contact'

/** One row of shell output. Data, not markup, so the shell is testable alone. */
export type Row =
  | { kind: 'text'; text: string; muted?: boolean }
  | { kind: 'entry'; name: string; note?: string; to?: To; hash?: string; href?: string; run?: string }
  | { kind: 'gap' }
  | { kind: 'error'; text: string }

export type File = { kind: 'file'; name: string; doc: Row[] }

export type Dir = {
  kind: 'dir'
  /** The path segment. `~` at the root. */
  name: string
  to: To
  /** What the site calls this destination, on its button. */
  label: string
  /** The `README.md` every directory carries. The file node below it is built
      from this same array, so the two cannot drift. */
  doc: Row[]
  children: (Dir | File)[]
}

export type Node = Dir | File

/* ------------------------------------------------------------- documents */

const WHOAMI = 'fran, full-stack engineer. TypeScript, Node.js, React, Rust, Linux, Bitcoin, PostgreSQL'

const GITHUB_ROW: Row = { kind: 'entry', name: GITHUB_HANDLE, href: GITHUB_URL }

const README_HOME: Row[] = [
  { kind: 'text', text: WHOAMI },
  { kind: 'entry', name: 'work', note: 'what I built', to: '/about', hash: 'sec-work' },
  GITHUB_ROW,
]

const README_ABOUT: Row[] = [
  { kind: 'entry', name: 'work.md', note: 'what I built', run: 'cat ~/about/work.md' },
  { kind: 'entry', name: 'stack', note: 'what I work with', to: '/', hash: 'sec-stack' },
  GITHUB_ROW,
]

/* The work history, straight from the page's own data. */
const WORK_DOC: Row[] = WORK.flatMap(
  (job): Row[] => [
    { kind: 'text', text: `${job.when} · ${job.what}` },
    { kind: 'text', text: job.body },
    { kind: 'text', text: job.stack, muted: true },
    { kind: 'gap' },
  ],
).slice(0, -1)

const README_PRODUCTS: Row[] = [
  ...CATALOG.map((item): Row => ({ kind: 'entry', name: item.name, note: item.tag, href: item.url })),
  { kind: 'entry', name: STUDIO.name, note: STUDIO.desc, href: STUDIO.url },
  GITHUB_ROW,
]

const README_PROJECTS: Row[] = [
  ...PACKAGES.map((pkg): Row => ({ kind: 'entry', name: pkg.name, note: 'npm', href: pkg.url })),
  { kind: 'entry', name: 'all repositories', note: GITHUB_HANDLE, href: GITHUB_URL },
]

const README_TIPS: Row[] = [GITHUB_ROW]
const README_CONTACT: Row[] = [GITHUB_ROW]

/* ------------------------------------------------------------------ tree */

type DirSpec = Omit<Dir, 'kind' | 'children'> & { entries?: (Dir | File)[] }

/** Every directory carries its README, so `ls` always lists something readable. */
const asDir = ({ entries = [], ...rest }: DirSpec): Dir => ({
  kind: 'dir',
  ...rest,
  children: [{ kind: 'file', name: 'README.md', doc: rest.doc }, ...entries],
})

const TREE: Dir = asDir({
  name: '~',
  to: '/',
  label: 'whoami',
  doc: README_HOME,
  entries: [
    asDir({
      name: 'about',
      to: '/about',
      label: 'about',
      doc: README_ABOUT,
      entries: [{ kind: 'file', name: 'work.md', doc: WORK_DOC }],
    }),
    asDir({ name: 'products', to: '/products', label: 'products', doc: README_PRODUCTS }),
    asDir({ name: 'projects', to: '/projects', label: 'projects', doc: README_PROJECTS }),
    asDir({ name: 'tips', to: '/tips', label: 'tips', doc: README_TIPS }),
    asDir({ name: 'contact', to: '/contact', label: 'contact', doc: README_CONTACT }),
  ],
})

const subdirs = (dir: Dir): Dir[] => dir.children.filter((child): child is Dir => child.kind === 'dir')

export const DESTINATIONS: Dir[] = [TREE, ...subdirs(TREE)]

/** Whether a path is one of the site's pages, as opposed to nothing at all. */
export function isDestination(route: string): boolean {
  return DESTINATIONS.some((dir) => dir.to === route)
}

/** The route's directory. Unknown routes fall back to the root. */
export function dirFor(route: string): Dir {
  return DESTINATIONS.find((dir) => dir.to === route) ?? TREE
}

function chainTo(target: Dir): Dir[] {
  const walk = (dir: Dir, trail: Dir[]): Dir[] | null => {
    const chain = [...trail, dir]
    if (dir === target) return chain
    for (const child of subdirs(dir)) {
      const found = walk(child, chain)
      if (found) return found
    }
    return null
  }
  return walk(TREE, []) ?? [TREE]
}

function pathOfChain(chain: Dir[]): string {
  return chain.length === 1 ? '~' : `~/${chain.slice(1).map((dir) => dir.name).join('/')}`
}

/** Where the prompt stands: `~`, `~/products`, and so on. */
export function pathFor(dir: Dir): string {
  return pathOfChain(chainTo(dir))
}

const cdCommand = (dir: Dir): string => `cd ${pathFor(dir)}`

/** Every destination in menu order: its route, its label and the command that lands on it. */
export const ROUTES = DESTINATIONS.map((dir) => ({ to: dir.to, label: dir.label, cmd: cdCommand(dir) }))

/* ----------------------------------------------------------------- paths */

export type Hit = { path: string; node: Node; parent: Dir }

/**
 * Resolves `~`, `/`, `.`, `..` and a relative path against the route's own
 * directory. Names are matched without case, because a portfolio terminal is
 * not fussy and the old resolver was not either. `null` is "no such file".
 */
export function resolvePath(route: string, input: string): Hit | null {
  const text = input.trim()
  /* `~` and `~/x` mean home; `/x` means the root of this tree, which is home;
     a leading tilde anywhere else is not a path form and resolves to nothing. */
  const home = text === '~' || text.startsWith('~/')
  const rooted = home || text.startsWith('/')
  const segments = (rooted ? text.slice(1) : text)
    .split('/')
    .filter((part) => part !== '' && part !== '.')

  let chain = chainTo(rooted ? TREE : dirFor(route))

  for (const [index, part] of segments.entries()) {
    const segment = part.toLowerCase()
    if (segment === '..') {
      if (chain.length > 1) chain = chain.slice(0, -1)
      continue
    }
    const current = chain[chain.length - 1]
    const child = current.children.find((node) => node.name.toLowerCase() === segment)
    if (!child) return null
    if (child.kind === 'dir') {
      chain = [...chain, child]
      continue
    }
    /* A file is only a valid last segment: `cat a/b.md/` reads, `cat b.md/x` does not. */
    if (index !== segments.length - 1) return null
    return { path: `${pathOfChain(chain)}/${child.name}`, node: child, parent: current }
  }

  const dir = chain[chain.length - 1]
  return { path: pathOfChain(chain), node: dir, parent: dir }
}

/* -------------------------------------------------------------- commands */

export const SHELL = [
  { name: 'ls', usage: 'ls [path]', note: 'list a directory' },
  { name: 'cd', usage: 'cd [path]', note: 'go to a directory' },
  { name: 'cat', usage: 'cat <file>', note: 'print a file' },
  { name: 'open', usage: 'open <path>', note: 'open it in the site' },
  { name: 'pwd', usage: 'pwd', note: 'print the working directory' },
  { name: 'whoami', usage: 'whoami', note: 'who I am' },
  { name: 'clear', usage: 'clear', note: 'clear this log' },
  { name: 'help', usage: 'help', note: 'this list' },
  { name: './init', usage: './init', note: 'replay the intro' },
] as const

export type Effect =
  | { kind: 'navigate'; to: To }
  | { kind: 'print'; rows: Row[] }
  | { kind: 'clear' }
  | { kind: 'init' }

const print = (rows: Row[]): Effect => ({ kind: 'print', rows })

/**
 * A destination named from the wrong directory is a dead end in a real shell,
 * so the error carries the command that would have worked. Empty when the name
 * is not a destination at all.
 */
function suggestion(target: string): Row[] {
  const name = target.trim().toLowerCase()
  if (!name || name.includes('/')) return []
  const dir = DESTINATIONS.find((destination) => destination.name.toLowerCase() === name)
  if (!dir) return []
  const command = cdCommand(dir)
  return [{ kind: 'entry', name: command, run: command }]
}

const fail = (text: string, target = ''): Effect =>
  print([{ kind: 'error', text }, ...suggestion(target)])

/** The listing of a directory: files run, directories go. */
function listing(dir: Dir, path: string): Row[] {
  return dir.children.map((child): Row =>
    child.kind === 'dir'
      ? { kind: 'entry', name: `${child.name}/`, to: child.to }
      : { kind: 'entry', name: child.name, run: `cat ${path}/${child.name}` },
  )
}

const helpRows = (): Row[] => SHELL.map((entry): Row => ({ kind: 'entry', name: entry.usage, note: entry.note }))

/**
 * Runs one line against the route the visitor is on. Pure: the caller renders
 * the effect, so `cd products` and a click on the products button end the same
 * way.
 */
export function run(route: string, raw: string): Effect {
  const [head, ...args] = raw.trim().split(/\s+/)
  if (!head) return print([])

  const first = args[0] ?? ''
  switch (head.toLowerCase()) {
    case 'ls': {
      if (args.length > 1) return fail('ls: too many arguments')
      const hit = resolvePath(route, first)
      if (!hit) return fail(`ls: ${first}: no such file or directory`, first)
      if (hit.node.kind === 'file') return print([{ kind: 'entry', name: hit.node.name, run: `cat ${hit.path}` }])
      return print(listing(hit.node, hit.path))
    }
    case 'cd': {
      if (args.length > 1) return fail('cd: too many arguments')
      const target = first || '~'
      const hit = resolvePath(route, target)
      if (!hit) return fail(`cd: ${target}: no such file or directory`, target)
      if (hit.node.kind === 'file') return fail(`cd: not a directory: ${target}`)
      return { kind: 'navigate', to: hit.node.to }
    }
    case 'cat': {
      if (args.length === 0) return fail('cat: missing operand')
      const rows: Row[] = []
      for (const target of args) {
        const hit = resolvePath(route, target)
        if (!hit) return fail(`cat: ${target}: no such file or directory`, target)
        if (hit.node.kind === 'dir') return fail(`cat: ${target}: is a directory`)
        if (rows.length > 0) rows.push({ kind: 'gap' })
        rows.push(...hit.node.doc)
      }
      return print(rows)
    }
    case 'open': {
      if (args.length === 0) return fail('open: missing operand')
      if (args.length > 1) return fail('open: too many arguments')
      const hit = resolvePath(route, first)
      if (!hit) return fail(`open: ${first}: no such file or directory`, first)
      /* A file opens the page it belongs to: its parent is that directory. */
      return { kind: 'navigate', to: hit.parent.to }
    }
    case 'pwd':
      return print([{ kind: 'text', text: pathFor(dirFor(route)) }])
    case 'whoami':
      return print([{ kind: 'text', text: WHOAMI }])
    case 'clear':
      return { kind: 'clear' }
    case 'help':
      return print(helpRows())
    case './init':
    case 'init':
      return { kind: 'init' }
    default: {
      /* zsh's AUTO_CD: a directory name on its own goes there. */
      const hit = args.length === 0 ? resolvePath(route, head) : null
      if (hit && hit.node.kind === 'dir') return { kind: 'navigate', to: hit.node.to }
      return fail(`command not found: ${head}`, head)
    }
  }
}

/* ------------------------------------------------------------ completion */

/** What a command can actually take: `cd` only directories, `cat` only files. */
function entryNames(dir: Dir, command: string): string[] {
  const names = dir.children.map((child) => (child.kind === 'dir' ? `${child.name}/` : child.name))
  if (command === 'cd') return names.filter((name) => name.endsWith('/'))
  if (command === 'cat') return names.filter((name) => !name.endsWith('/'))
  return names
}

/**
 * What Tab can complete to, in list order: the command word first from the
 * shell and the working directory, then a path argument from wherever it
 * points. `cd pro` offers both `cd products/` and `cd projects/`.
 */
export function completions(route: string, value: string): string[] {
  if (!value.trim()) return []
  const space = value.lastIndexOf(' ')
  const head = space === -1 ? '' : value.slice(0, space + 1)
  const word = space === -1 ? value : value.slice(space + 1)

  if (space === -1) {
    const names = [...SHELL.map((entry) => entry.name), ...subdirs(dirFor(route)).map((dir) => dir.name)]
    return names.filter((name) => name.toLowerCase().startsWith(word.toLowerCase()))
  }

  const command = head.trim().split(/\s+/)[0].toLowerCase()
  const slash = word.lastIndexOf('/')
  const dirPart = slash === -1 ? '' : word.slice(0, slash + 1)
  const partial = slash === -1 ? word : word.slice(slash + 1)

  let dir = dirFor(route)
  if (dirPart) {
    const base = resolvePath(route, dirPart)
    if (!base || base.node.kind !== 'dir') return []
    dir = base.node
  }

  return entryNames(dir, command)
    .filter((name) => name.toLowerCase().startsWith(partial.toLowerCase()))
    .map((name) => head + dirPart + name)
}

/** A completion in progress: the candidates, how far in Tab is, and the
    directory they belong to. */
export type Completing = { list: string[]; index: number; route: string }

/**
 * One Tab press. Tab belongs to the terminal, so it always answers with
 * something to show and never throws the visitor out of the field; Shift+Tab is
 * the way out. A word with several endings is walked one press at a time and
 * wraps, an empty line lists what can be typed, and a line that is already
 * complete is left alone.
 */
export function tab(
  route: string,
  value: string,
  previous: Completing | null,
): Completing & { value: string } {
  if (!value.trim()) {
    return { list: SHELL.map((entry) => entry.name), index: -1, value, route }
  }
  /* Walk on only while the line still holds the candidate that was put there,
     in the directory it was put there for: a changed line or a changed route
     recomputes instead of inserting something stale. */
  if (previous && previous.route === route && previous.index >= 0 && previous.list[previous.index] === value) {
    const index = (previous.index + 1) % previous.list.length
    return { list: previous.list, index, value: previous.list[index], route }
  }
  const list = completions(route, value)
  if (list.length === 0 || (list.length === 1 && list[0] === value)) {
    return { list: [], index: -1, value, route }
  }
  return { list, index: 0, value: list[0], route }
}
